<template>
  <BModal
    ref="termModal"
    size="xl"
    title="Assign Cascading of Customer Memos"
    scrollable
    centered
    no-close-on-esc
    no-enforce-focus
    no-close-on-backdrop
    aria-hidden="false"
    role="dialog"
  >
    <BRow>
      <BCol
        cols="12"
        md="3"
      >
        <ValidationProvider
          v-slot="{ errors }"
          vid="month"
          name="month"
          rules="required"
        >
          <BFormGroup
            label="Month"
            label-for="fMonth"
            label-class="font-weight-bolder"
            class="mb-2"
          >
            <flat-pickr
              id="fMonth"
              v-model="formMemo.schedule"
              :class="['form-control', {
                'is-invalid': errors.length > 0
              }]"
              placeholder="--------- ----"
              :config="{
                ...flatPickerConfig,
                altInput: false
              }"
              :disabled="state.busy"
            />
            <div
              v-if="(errors.length > 0)"
              class="invalid-feedback"
              v-text="errors[0]"
            />
          </BFormGroup>
        </ValidationProvider>
      </BCol>

      <BCol
        cols="12"
        md="9"
      >
        <ValidationProvider
          v-slot="{ errors }"
          vid="memo_id"
          name="memo"
          rules="required"
        >
          <BFormGroup
            label="Memo"
            label-for="fMemo"
            label-class="font-weight-bolder"
            class="mb-2"
          >
            <VSelect
              v-model="selected.memo"
              input-id="fMemo"
              label="memo_name"
              clearable
              append-to-body
              placeholder="select memo here"
              :options="options.memos"
              :disabled="[
                fetching.memos,
                state.busy
              ].some(isTrue => isTrue)"
              :loading="fetching.memos"
              :class="{ 'is-invalid': errors.length > 0 }"
              @search="searchMemos"
            >

              <template #option="{ memo_name, team, creator }">
                <div class="pb-1">
                  <div
                    class="text-break text-wrap"
                    style="font-size: 13px;"
                    v-text="memo_name"
                  />
                  <div
                    v-if="team?.id && creator?.id"
                    style="font-size: 10px;"
                    class="font-weight-bolder"
                  >
                    {{ getFullName(null, null, creator) }} - {{ team.team_name }}
                  </div>
                </div>
              </template>
              <template #selected-option="{ memo_name }">
                <div
                  class="w-225px w-md-325px text-truncate"
                  v-text="memo_name"
                />
              </template>
              <template #no-options="">
                no available memo
              </template>
            </VSelect>
            <div
              v-if="(errors.length > 0)"
              class="invalid-feedback"
              v-text="errors[0]"
            />
          </BFormGroup>
        </ValidationProvider>
      </BCol>
      <BCol
        cols="12"
        class="min-h-400px"
      >
        <MultipleMemoTable
          ref="memoTable"
          :fetching="fetching.fsOfficers"
          :fsos="options.fsOfficers"
          :details="formMemo.customer_memos"
        />
      </BCol>
    </BRow>

    <template #modal-footer="{ cancel }">
      <BButton
        size="sm"
        variant="primary"
        :disabled="state.busy"
        @click.prevent="validateMemo"
      >
        Assign
      </BButton>
      <BButton
        size="sm"
        variant="outline-dark"
        :disabled="state.busy"
        @click.prevent="discardBtnEvent => preventAriaHidden(discardBtnEvent, cancel)"
      >
        Discard
      </BButton>
    </template>
  </BModal>
</template>
<script>
import { clone } from 'lodash'
import { mapGetters } from 'vuex'
import FPMonthSelect from 'flatpickr/dist/plugins/monthSelect'
import moment from 'moment'

import MultipleMemoTable from '@/views/asm/customer-memos/components/memo-table/MultipleMemoTable.vue'

import MxMisc from '@/mixins/misc'
import MxFormatter from '@/mixins/formatter'
import MFSOfficers from '@/mixins/accounts/fsos'
import MMemos from '@/mixins/maintenances/memos'

import { SASMFsoCustomerMemo } from '@/services/asm'

export default {
  name: 'ASMCustomerMemosMultipleCreate',

  components: {
    MultipleMemoTable
  },

  mixins: [
    MFSOfficers,
    MxMisc,
    MxFormatter,
    MMemos
  ],

  emits: [
    'update:rows'
  ],

  data () {
    return {
      state: {
        busy: false
      },
      flatPickerConfig: {
        appendTo: document.body,
        altInput: true,
        allowInput: true,
        disableMobile: true,
        plugins: [
          new FPMonthSelect({
            dateFormat: 'Y-m',
            altFormat: 'F Y'
          })
        ]
      },
      formMemo: {
        schedule: '',
        memo_id: '',
        customer_memos: []
      }
    }
  },

  computed: {
    ...mapGetters({
      currentUser: 'auth/user'
    })
  },

  watch: {
    'selected.memo' (memo) {
      this.formMemo.memo_id = memo?.id || ''
    }
  },

  methods: {
    openMultiple () {
      this.getFSOfficers(this, '', this.currentUser.team, this.currentUser.area)
      this.getMemos(this)

      this.formMemo = Object.assign({}, this.formMemo, {
        schedule: moment().format('YYYY-MM'),
        memo_id: '',
        customer_memos: Array.from({ length: 5 }, () => ({
          officer: null,
          cluster: null,
          customer: null
        }))
      })

      this.$refs.termModal.show()
    },

    validateMemo () {
      this.$refs.memoTable.validate().then(
        async () => {
          return this.swalConfirm({
            html: 'Assign Cascading of Customer Memos?',
            confirmButtonText: 'Assign',
            preConfirm: () => {
              this.state.busy = true

              return this.putTerms().catch(({ message }) => {
                if (message && message?.errors) {
                  this.$refs.memoTable.setErrors(message.errors)
                }
              }).finally(() => {
                this.state.busy = false
              })
            }
          })
        }
      ).catch(
        () => {
          this.swalInvalid()
        }
      )
    },

    async putTerms () {
      const payload = Object.assign(clone(this.formMemo), {
        customer_memos: this.$refs.memoTable.getCustomerMemos(this.formMemo.schedule)
      })

      return SASMFsoCustomerMemo.uploads(payload).then(
        ({ data }) => {
          this.swalSuccess(data.message).then(() => {
            this.$refs.termModal.hide()

            this.$emit('update:rows')
          })

          return data
        }
      )
    }
  }
}
</script>
